import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import { Clear as ClearIcon, ContentCopy } from "@mui/icons-material";
import { format } from "date-fns";
import { isEmpty } from "lodash";
import { useEffect, useMemo, useState } from "react";
import { useDebounce } from "react-use";
import { createActor, waitFor } from "xstate";

import { makeToast } from "@moment/design-system/Toast";

import { useAuth } from "~/auth/useAuth";
import { useSyncDocument } from "~/components/canvas/properties-pane/publish-tab/usePublish";
import { copyContent } from "~/components/common/CopyText";
import { useRouteParams } from "~/data/route";
import { useDocumentWorkspaces, usePersonalWorkspace, useSharingSyncState } from "~/state";
import { searchUserActor } from "~/state/machines/docsapi/searchUsers.machine";
import {
	type Invitation,
	type PermRelation,
	type SearchUserResult,
	type UpdateUserPermission,
} from "~/state/types";
import { Avatar, AvatarFallback, AvatarImage } from "~/ui/Avatar";
import { IconButton } from "~/ui/Button";
import { MomentComboboxButton } from "~/ui/Button/ComboboxButton";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "~/ui/Card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "~/ui/Select";
import { Separator } from "~/ui/Separator";

type SelectedUser = SearchUserResult & {
	invite?: boolean;
	inviteEmail?: string;
	relation: PermRelation;
};

type UserWithPermission = SearchUserResult & {
	relation: PermRelation;
};

type InvitePermission = Invitation & {
	relation: PermRelation;
	type: "grant" | "revoke";
};

const isValidEmail = (email: string | undefined): boolean => {
	if (email === undefined) {
		return false;
	}
	return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

type PermSelection = PermRelation | "remove";

const getUserInitials = (user: SearchUserResult): string => {
	const split = user.name.split(" ");
	if (split === undefined || split.length === 0) {
		return "";
	}
	if (split.length === 1) {
		return split[0]?.charAt(0).toUpperCase() || "";
	}

	const firstInitial = split[0]?.charAt(0).toUpperCase() || "";
	const lastInitial = split[1]?.charAt(0).toUpperCase() || "";
	return `${firstInitial}${lastInitial}`;
};

export const ShareTab = () => {
	const { documentId } = useRouteParams();
	const { user: loggedInUser } = useAuth();

	const {
		permissions,
		attachWorkspace,
		sharingSyncActor,
		isLoading: isSharingStateLoading,
		isError: isSharingStateError,
		attachedWorkspace,
		errorState,
		refresh,
		updateInvite,
		updatePermission,
	} = useSharingSyncState(documentId);

	const remoteWorkspaces = useDocumentWorkspaces(documentId);
	// const { data: availableWorkspaces } = useWorkspaces();
	const personalWorkspace = usePersonalWorkspace(loggedInUser);

	const [query, setQuery] = useState("");
	const [isLoading, setIsLoading] = useState(false);
	const [selectedUser, setSelectedUser] = useState<SelectedUser | undefined>(undefined);
	const [searchUserData, setSearchUserData] = useState<SearchUserResult[]>([]);
	const [allUserData, setAllUserData] = useState<SearchUserResult[]>([]);
	const [accessList, setAccessList] = useState<Map<string, UserWithPermission>>(new Map());
	const [inviteList, setInviteList] = useState<Invitation[]>([]);
	const [loaded, setLoaded] = useState(false);

	// in order to avoid doing consistent reads on authzed, which are expensive,
	// we refresh after a delay so the system has a chance to propagate the new update.
	const refreshPermissionWithDelay = () => {
		setTimeout(() => {
			refresh();
		}, 15000);
	};

	const { isSyncing, canSync, sync, planStatus, publishUrl } = useSyncDocument({
		mode: "share-sync",
	});

	const noRemote = useMemo(() => {
		const noRemote =
			remoteWorkspaces === undefined ||
			remoteWorkspaces?.length === 0 ||
			remoteWorkspaces[0]?.name === undefined;
		if (!noRemote && isEmpty(attachedWorkspace) && remoteWorkspaces[0]?.name !== undefined) {
			const workspaceName = remoteWorkspaces[0]?.name;
			attachWorkspace(workspaceName);
		}
		return noRemote;
	}, [remoteWorkspaces, attachWorkspace, attachedWorkspace]);

	useEffect(() => {
		// This is gross but this is because authzed consistency reads are expensive.
		// eventually, we should do some caching here to avoid this kind of logic,
		// but for now, we do this to avoid a confusing UI experience.
		if (loaded) {
			return;
		}

		const accessList = new Map<string, UserWithPermission>();
		const inviteList = new Array<Invitation>();
		if (permissions !== undefined && permissions !== null) {
			// we need to convert the permissions to a map
			permissions.read?.forEach((entry: SearchUserResult) => {
				accessList.set(entry.user_id, {
					...entry,
					relation: "reader",
				});
			});

			permissions.write?.forEach((entry: SearchUserResult) => {
				accessList.set(entry.user_id, {
					...entry,
					relation: "collaborator",
				});
			});

			permissions.own?.forEach((entry: SearchUserResult) => {
				accessList.set(entry.user_id, {
					...entry,
					relation: "owner",
				});
			});

			permissions.invite?.forEach((entry: Invitation) => {
				inviteList.push({
					...entry,
				});
			});
		}

		if (permissions === undefined && noRemote && loggedInUser) {
			// return the logged in user as the owner, as we are working in the local workspace.
			accessList.set(loggedInUser.id, {
				name: loggedInUser.name,
				nickname: loggedInUser.nickname,
				picture: loggedInUser.profilePicture,
				user_id: loggedInUser.id,
				relation: "owner",
			});
		}

		setAccessList(accessList);
		setInviteList(inviteList);

		// we don't want to refresh permissions all the time as it takes 15 seconds
		// per update to get the latest data.
		if (permissions !== undefined && !noRemote) {
			setLoaded(true);
		}
	}, [permissions, loggedInUser, noRemote, loaded]);

	const sortedAccessArray = useMemo(
		() => Array.from(accessList.entries()).sort(([, a], [, b]) => a.name.localeCompare(b.name)),
		[accessList]
	);

	// Actions
	const onInviteButton = async () => {
		if (sharingSyncActor === undefined) {
			return;
		}
		if (noRemote) {
			// TODO: make this an option.
			if (personalWorkspace === undefined) {
				return;
			}
			attachWorkspace(personalWorkspace.name);
			await sync(`Publishing changes ${format(new Date(), "yyyy/MM/dd")}`);
		}

		// check for error state.
		if (selectedUser?.invite) {
			updateInvite({
				email: selectedUser.inviteEmail,
				type: "grant",
				relation: selectedUser.relation,
			} as InvitePermission);
		}
		if (selectedUser?.user_id) {
			updatePermission({
				user: selectedUser as SearchUserResult,
				type: "grant",
				relation: selectedUser.relation,
			} as UpdateUserPermission);
		}

		waitFor(sharingSyncActor, (state) => state.matches("idle") || state.matches("error"))
			.then((state) => {
				if (state.matches("error")) {
					makeToast({
						message: "Error adding permission",
						variant: "error",
						timer: "timed-dismissible",
					});
					return;
				}
				refreshPermissionWithDelay();
			})
			.catch((e) => {
				console.error("Error adding permission", e);
				makeToast({
					message: "Error adding permission",
					variant: "error",
					timer: "timed-dismissible",
				});
			});

		if (selectedUser?.inviteEmail) {
			inviteList.push({
				email: selectedUser.inviteEmail,
				relation: selectedUser.relation,
				referrer: loggedInUser?.id || "",
			});
			setInviteList(inviteList);
		} else if (selectedUser?.user_id) {
			accessList.set(selectedUser.user_id, {
				...selectedUser,
				relation: selectedUser.relation,
			});
			setAccessList(new Map(accessList));
		}

		setSelectedUser(undefined);
		setQuery("");
	};

	const onPermissionChange = async (user: UserWithPermission, select: PermSelection) => {
		if (sharingSyncActor === undefined) {
			return;
		}
		const input = {
			user,
			type: select === "remove" ? "revoke" : "grant",
			relation: select === "remove" ? user.relation : (select as PermRelation),
		} as UpdateUserPermission;

		updatePermission(input);
		// optimistically
		if (select === "remove") {
			accessList.delete(user.user_id);
			setAccessList(new Map(accessList));
		} else {
			accessList.set(user.user_id, {
				...user,
				relation: select as PermRelation,
			});
			setAccessList(new Map(accessList));
		}
		waitFor(sharingSyncActor, (state) => state.matches("idle") || state.matches("error"))
			.then((state) => {
				if (state.matches("error")) {
					makeToast({
						message: "Error updating permission",
						variant: "error",
						timer: "timed-dismissible",
					});
					return;
				}

				refreshPermissionWithDelay();
			})
			.catch((e) => {
				makeToast({
					message: "Error updating permission",
					variant: "error",
					timer: "timed-dismissible",
				});
				console.error("Error updating permission", e);
			});
	};

	const onInviteRemove = async (invite: Invitation, select: PermSelection) => {
		if (sharingSyncActor === undefined) {
			return;
		}

		if (select !== "remove") {
			return;
		}
		const input = {
			...invite,
			type: "revoke",
		} as InvitePermission;

		updateInvite(input);
		waitFor(sharingSyncActor, (state) => state.matches("idle") || state.matches("error"))
			.then((state) => {
				if (state.matches("error")) {
					makeToast({
						message: "Error removing invite",
						variant: "error",
						timer: "timed-dismissible",
					});
					return;
				}
			})
			.catch((e) => {
				console.error("Error removing invite", e);
				makeToast({
					message: "Error removing invite",
					variant: "error",
					timer: "timed-dismissible",
				});
			});

		const newInviteList = inviteList.filter((i) => i.email !== invite.email);
		setInviteList(newInviteList);
	};

	useDebounce(
		async () => {
			const actor = createActor(searchUserActor, { input: { query } });
			actor.start();

			setIsLoading(true);
			const result = (await waitFor(actor, (state) => state.status === "done")).output;
			setIsLoading(false);
			if (result === null || result === undefined) {
				return;
			}

			// do not show users who already have access
			const filterResult = result.filter((user) => !accessList.has(user.user_id));

			setSearchUserData(filterResult);
			setAllUserData(result);
		},
		100,
		[query]
	);

	const disableInviteButton = useMemo(() => {
		if (sharingSyncActor === undefined) {
			return true;
		}
		if (selectedUser === undefined) {
			return true;
		}
		if (
			selectedUser.invite === true &&
			selectedUser.inviteEmail !== undefined &&
			!isValidEmail(selectedUser.inviteEmail)
		) {
			return true;
		}

		if (isSharingStateError) {
			console.error("error in sharing state", { errorState });
			return true;
		}

		// This is a bit confusing, but we want to disable invite if the sync machine is cannot sync EXCEPT
		// for when the status is "unachievable-nothing-to-do". canSync returns false if the document has no remote,
		// which means that this document has never been shared or published before.
		// if that is the case, we should allow the invite to go through and have syncMachine create a new remote repo for us.
		if (!(canSync || planStatus === "unachievable-nothing-to-do")) {
			return true;
		}

		return isLoading || isSharingStateLoading || isSyncing;
	}, [
		selectedUser,
		isSharingStateError,
		isSharingStateLoading,
		isLoading,
		errorState,
		canSync,
		planStatus,
		isSyncing,
		sharingSyncActor,
	]);

	return (
		<Card>
			<CardHeader className="pb-3">
				<CardTitle>Share this document</CardTitle>
				{remoteWorkspaces?.length === 0 && personalWorkspace && (
					<CardDescription>
						A remote repo will be created on your personal cloud at{" "}
						<code className="font-medium">
							{personalWorkspace.host}/git/{personalWorkspace?.slug}
						</code>
					</CardDescription>
				)}
				{!noRemote && publishUrl && (
					<CardDescription>
						<div className="flex items-center gap-2">
							<div className="truncate text-secondary">{publishUrl}</div>
							<IconButton
								className="shrink-0"
								size="xs"
								preset="quaternary"
								icon={<ContentCopy />}
								label="Copy"
								onClick={() => copyContent(publishUrl)}
							/>
						</div>
					</CardDescription>
				)}
			</CardHeader>
			<CardContent>
				<Combobox
					as="div"
					onChange={async (value: SelectedUser) => {
						if (value === null || value === undefined) {
							return;
						}

						// do not set selection if invite email is not valid
						if (value.invite === true && !isValidEmail(value.inviteEmail)) {
							return;
						}

						setSelectedUser(value);
					}}
					className="w-full relative"
					immediate
				>
					<div className="flex items-center">
						<div className="flex flex-row justify-center flex-grow">
							{/* Right now, we only support inviting one user at a time. */}
							{!selectedUser && (
								<ComboboxInput
									aria-label="Enter email or name"
									placeholder="Enter email or name"
									spellCheck={false}
									onChange={(e) => {
										setQuery(e.currentTarget.value);
									}}
									className="flex flex-row h-8 rounded-md border bg-transparent py-1.5 pl-3 pr-10 sm:text-sm/6 focus-within:outline-palette-brand-9 outline-1 -outline-offset-1 focus-within:outline focus-within:outline-2 focus-within:-outline-offset-2"
								/>
							)}
							{selectedUser && (
								<div className="flex flex-row items-center space-x-2">
									{selectedUser.invite === true && (
										<div>{selectedUser.inviteEmail}</div>
									)}
									{!isEmpty(selectedUser.user_id) && (
										<div className="inline-flex flex-row items-center space-x-1">
											<Avatar size="sm">
												<AvatarImage
													src={selectedUser.picture}
													alt="Image"
												/>
												<AvatarFallback>
													{getUserInitials(selectedUser)}
												</AvatarFallback>
											</Avatar>

											<div>
												<p className="text-sm text-muted-foreground">
													{selectedUser.name}
												</p>
											</div>
										</div>
									)}
									<Select
										defaultValue={selectedUser.relation}
										onValueChange={(value) => {
											setSelectedUser({
												...selectedUser,
												relation: value as PermRelation,
											});
										}}
									>
										<SelectTrigger
											className="ml-auto w-[110px]"
											aria-label="Edit"
										>
											<SelectValue />
										</SelectTrigger>

										<SelectContent>
											<SelectItem value="collaborator">Can edit</SelectItem>
											<SelectItem value="reader">Can view</SelectItem>
										</SelectContent>
									</Select>
									<IconButton
										size="xs"
										label="Remove"
										onClick={() => {
											setSelectedUser(undefined);
										}}
										icon={<ClearIcon />}
									/>
								</div>
							)}
							<MomentComboboxButton
								className="ml-auto"
								onClick={onInviteButton}
								disabled={disableInviteButton}
							>
								Invite
							</MomentComboboxButton>
						</div>
					</div>
					{!isLoading && query !== "" && (
						<ComboboxOptions className={"flex flex-col w-full rounded-md p-2 border"}>
							{searchUserData &&
								searchUserData.length > 0 &&
								searchUserData.map((user: SearchUserResult) => (
									<ComboboxOption
										key={user.user_id}
										value={{
											...user,
											relation: "reader",
										}}
									>
										<div className="inline-flex flex-row items-center space-x-1">
											<Avatar size="sm">
												<AvatarImage src={user.picture} alt="Image" />
												<AvatarFallback>
													{getUserInitials(user)}
												</AvatarFallback>
											</Avatar>

											<div>
												<p className="text-sm text-muted-foreground">
													{user.name}
												</p>
											</div>
										</div>
									</ComboboxOption>
								))}
							{searchUserData.length === 0 &&
								query !== "" &&
								!isValidEmail(query) && (
									<div className="inline-flex flex-row items-center space-x-1">
										Keep typing an email to invite
									</div>
								)}
							{allUserData.length === 0 && query !== "" && isValidEmail(query) && (
								<ComboboxOption
									key="invite_user"
									value={{
										invite: true,
										inviteEmail: query,
										relation: "reader",
									}}
								>
									Invite <div className="inline text-sm font-medium">{query}</div>
								</ComboboxOption>
							)}
							{allUserData.length !== 0 &&
								searchUserData.length === 0 &&
								query !== "" &&
								isValidEmail(query) && (
									<ComboboxOption
										key="invite_user"
										value={{
											invite: true,
											inviteEmail: query,
											relation: "reader",
										}}
									>
										<div className="inline text-sm font-medium">{query}</div>{" "}
										already has access
									</ComboboxOption>
								)}
						</ComboboxOptions>
					)}
				</Combobox>

				{sortedAccessArray.length > 0 && (
					<>
						<Separator className="my-4" />
						<div className="space-y-4">
							<div className="text-sm font-medium">People with access</div>
							<div className="grid gap-6">
								{sortedAccessArray.map(([key, user]) => {
									return (
										<div
											key={key}
											className="flex items-center justify-between space-x-4"
										>
											<div className="flex items-center space-x-4">
												<Avatar>
													<AvatarImage src={user.picture} alt="Image" />
													<AvatarFallback>
														{getUserInitials(user)}
													</AvatarFallback>
												</Avatar>
												<div>
													<p className="text-sm font-medium leading-none">
														{user.name}{" "}
														{user.user_id === loggedInUser?.id &&
															"(you)"}
													</p>
													<p className="text-sm text-muted-foreground">
														{user.nickname}
													</p>
												</div>
											</div>
											{user.relation === "owner" ? (
												<div className="text-sm font-medium text-muted-foreground">
													Owner
												</div>
											) : (
												<Select
													defaultValue={user.relation}
													onValueChange={async (value) => {
														await onPermissionChange(
															user,
															value as PermSelection
														);
													}}
												>
													<SelectTrigger
														className="ml-auto w-[110px]"
														aria-label="Edit"
													>
														<SelectValue />
													</SelectTrigger>
													<SelectContent>
														<SelectItem value="collaborator">
															Can edit
														</SelectItem>
														<SelectItem value="reader">
															Can view
														</SelectItem>
														<SelectItem value="remove">
															Remove
														</SelectItem>
													</SelectContent>
												</Select>
											)}
										</div>
									);
								})}
							</div>
						</div>
					</>
				)}

				{inviteList.length > 0 && (
					<>
						<Separator className="my-4" />
						<div className="space-y-4">
							<div className="text-sm font-medium">People you invited</div>
							<div className="grid gap-6">
								{inviteList.map((invite) => {
									return (
										<div
											key={invite.email}
											className="flex items-center justify-between space-x-4"
										>
											<div className="flex items-center space-x-4">
												<div>
													<p className="text-sm font-medium leading-none">
														{invite.email}
													</p>
												</div>
											</div>
											<Select
												defaultValue={invite.relation}
												onValueChange={async (value) => {
													await onInviteRemove(
														invite,
														value as PermSelection
													);
												}}
											>
												<SelectTrigger
													className="ml-auto w-[110px]"
													aria-label="Edit"
												>
													<SelectValue placeholder="Select" />
												</SelectTrigger>
												<SelectContent>
													<SelectItem value="reader">Can view</SelectItem>
													<SelectItem value="collaborator">
														Can edit
													</SelectItem>
													<SelectItem value="remove">Remove</SelectItem>
												</SelectContent>
											</Select>
										</div>
									);
								})}
							</div>
						</div>
					</>
				)}
			</CardContent>
		</Card>
	);
};
